<template>
	<QuestionGroup>
		<QuestionCard
			:title="endpointsCardTitle"
			:description="selectedProviderRedirectTooltip"
		>
			<v-row dense>
				<v-col cols="6">
					<TextField
						:value.sync="ssoConfiguration.authority"
						:label="selectedProviderAuthorityDisplayName"
						:rules="ssoConfiguration.isEnabled ? { required: true, max: 512 } : { max: 512 }"
						test-id="sso-configuration-redirect-uri"

						:disabled="!userFullPermissions"
						@input="updateSsoConfiguration('authority', $event)"
					/>
				</v-col>
			</v-row>
		</QuestionCard>
		<QuestionCard
			:title="signOutCardTitle"
			:description="selectedProviderSignOutTooltip"
		>
			<v-row dense>
				<v-col cols="6">
					<TextField
						:value.sync="ssoConfiguration.remoteLogoutEndpointUrl"
						:label="selectedProviderRemoteLogoutEndpointUrlDisplayName"
						:rules="ssoConfiguration.isEnabled ? { required: true, max: 512 } : { max: 512 }"
						test-id="sso-configuration-sign-out-redirect-uri"

						:disabled="!userFullPermissions"
						@input="updateSsoConfiguration('remoteLogoutEndpointUrl', $event)"
					/>
				</v-col>
			</v-row>
		</QuestionCard>

		<QuestionCard
			title="Which claim identifies the user? (e.g. email, sub, upn)"
			description="Enter the claim in the token that uniquely identifies the user and select the corresponding Syrenis field to match against the identifier value."
		>
			<v-row dense>
				<v-col cols="6">
					<TextField
						:value.sync="ssoConfiguration.identityClaimTypeName"
						:label="`Identity Provider User Claim Name${ssoConfiguration.isEnabled ? ' *' : ''}`"
						:rules="ssoConfiguration.isEnabled ? 'required' : ''"
						test-id="sso-configuration-identity-claim-type-name"
						:disabled="!userFullPermissions"
						@input="updateSsoConfiguration('identityClaimTypeName', $event)"
					/>
				</v-col>
				<v-col
					v-if="ssoConfiguration.productId === 2"
					cols="6"
				>
					<Dropdown
						:value="ssoConfiguration.ssoCspUserIdentityField"
						:items="[
							{ text: 'Email Address', value: 'EmailAddress' },
							{ text: 'Username', value: 'Username' }
						]"
						label="Syrenis User Mapping Field"
						test-id="sso-configuration-cassie-user-mapping-field"
						:disabled="!userFullPermissions"
						@change="updateSsoConfiguration('ssoCspUserIdentityField', $event)"
					/>
				</v-col>
			</v-row>
		</QuestionCard>

		<QuestionCard
			title="Which OpenID Connect scopes should Syrenis request? (e.g. openid, profile, email)"
			description="Add the scopes Syrenis should request from your identity provider."
		>
			<validation-provider
				#default="{ valid }"
				:rules="scopeFieldRules"
			>
				<v-row dense>
					<v-col cols="6">
						<TextField
							v-model="scope"
							label="Scope"
							test-id="sso-configuration-scope"

							:rules="scopeFieldRules"
							:disabled="!userFullPermissions"
							@keydown.enter="addScope(valid)"
						/>
					</v-col>
					<v-col cols="5">
						<PrimaryActionButton
							v-if="userFullPermissions"
							small-width
							:disabled="!scope"
							@click="addScope(valid)"
						>
							Add
						</PrimaryActionButton>
					</v-col>
				</v-row>
			</validation-provider>
			<v-row
				class="mt-2"
				dense
			>
				<v-col cols="6">
					<div class="selected-fields-chips">
						<v-chip
							v-for="(item, index) in scopeItems"
							:key="item.scopeName + '-' + index"
							small
							:close="userFullPermissions && index !== 0"
							class="mr-2 mb-2 square-chip"
							@click:close="removeScope(item.scopeName)"
						>
							{{ item.scopeName }}
						</v-chip>
					</div>
				</v-col>
			</v-row>
		</QuestionCard>
		<QuestionCard
			:title="forceConnectionDetails
				? `Let's finish connecting to ${selectedProviderName || 'your identity provider'} — Client ID and Client Secret are required to continue.`
				: 'Do you want to edit the connection details?'"
			:show-yes-no-buttons="userFullPermissions && !forceConnectionDetails"
			:value="ssoConfiguration.editConnectionDetails"
			@input="updateSsoConfiguration('editConnectionDetails', $event)"
		>
			<v-row
				v-if="ssoConfiguration.editConnectionDetails"
				dense
			>
				<v-col cols="6">
					<TextField
						:value.sync="ssoConfiguration.clientId"
						label="Client ID"
						test-id="sso-configuration-client-id"
						explainer="The Client ID is used by the SSO Provider to identify Syrenis during the authentication process"
						:disabled="!userFullPermissions"
						@input="updateSsoConfiguration('clientId', $event)"
					/>
				</v-col>
				<v-col cols="6">
					<TextField
						:value.sync="ssoConfiguration.clientSecret"
						label="Client Secret"
						test-id="sso-configuration-client-secret"
						explainer="This is a confidential string of characters shared between Syrenis and the SSO provider"
						:disabled="!userFullPermissions"
						@input="updateSsoConfiguration('clientSecret', $event)"
					/>
				</v-col>
			</v-row>
		</QuestionCard>
	</QuestionGroup>
</template>

<script>
import { QuestionGroup, QuestionCard } from '../../../../components/component-library-wrapper.js'
import TextField from '../../../../../../shared/components/text-field.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
const SSO_PROVIDER_IDS = {
	ADFS: 1,
	ENTRA_ID: 2,
	OKTA: 3,
	GOOGLE: 4
}
export default {
	components: {
		QuestionGroup,
		QuestionCard,
		TextField,
		PrimaryActionButton,
		Dropdown
	},
	props: {
		ssoConfiguration: Object,
		lookupData: Object,
		ssoConfigurationToManage: Object,
		userFullPermissions: Boolean,
		isEdit: Boolean,
		forceConnectionDetails: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			editConnectionDetails: false,
			scope: null
		}
	},
	computed: {
		selectedProviderName () {
			const provider = this.lookupData?.ssoProviders?.find(p => p.id === this.ssoConfiguration?.ssoProviderId)
			return provider?.ssoProviderName || ''
		},
		endpointsCardTitle () {
			const provider = this.lookupData.ssoProviders.find(p => p.id === this.ssoConfiguration.ssoProviderId)
			return provider
				? `What is the ${provider.ssoProviderName} authority (base URL) for OpenID Connect?`
				: 'What is the authority (base URL) for OpenID Connect?'
		},
		selectedProviderRemoteLogoutEndpointUrlDisplayName () {
			const ssoProvider = this.lookupData.ssoProviders.find(provider => provider.id === this.ssoConfiguration.ssoProviderId)
			return `${ssoProvider.remoteLogoutEndpointUrlDisplayName}${this.ssoConfiguration.isEnabled ? ' *' : ''}`
		},
		signOutCardTitle () {
			const provider = this.lookupData.ssoProviders.find(p => p.id === this.ssoConfiguration.ssoProviderId)
			const tooltip = this.selectedProviderSignOutTooltip || ''
			const usesLogout = /logout/i.test(tooltip)
			const action = usesLogout ? 'Logout' : 'Sign-out'
			return provider
				? `What is the ${provider.ssoProviderName} ${action} endpoint (session termination URL)?`
				: `What is the ${action} endpoint (session termination URL)?`
		},
		isPartialConfig () {
			return !this.ssoConfiguration.originalAuthority ||
           !this.ssoConfiguration.originalClientId ||
           !this.ssoConfiguration.identityClaimTypeName ||
           !this.ssoConfiguration.remoteLogoutEndpointUrl
		},
		selectedProviderAuthorityDisplayName () {
			const ssoProvider = this.lookupData.ssoProviders.find(provider => provider.id === this.ssoConfiguration.ssoProviderId)
			return `${ssoProvider.authorityDisplayName}${this.ssoConfiguration.isEnabled ? ' *' : ''}`
		},
		scopeItems () {
			const mappedScopes = this.ssoConfiguration?.scopes?.split(',').map(scope => ({
				scopeName: scope
			})).filter(scope => scope.scopeName !== '') ?? []
			return [
				{
					scopeName: 'openId'
				},
				...mappedScopes
			]
		},
		scopeFieldRules () {
			return {
				duplicateValues: { value: this.scopeItems.map(scope => scope.scopeName) },
				forbiddenChars: { value: [' ', ','] }
			}
		},
		selectedProviderRedirectTooltip () {
			if (!this.ssoConfiguration?.ssoProviderId) {
				return 'Base (authority) URL for OpenID Connect.'
			}
			switch (this.ssoConfiguration.ssoProviderId) {
				case SSO_PROVIDER_IDS.ADFS:
					return 'This is typically \'https://your-adfs-server.your-domain.com/adfs\' (or \'/adfs/oauth2\').'
				case SSO_PROVIDER_IDS.ENTRA_ID:
					return 'This is typically \'https://login.microsoftonline.com/{tenant-id}\'.'
				case SSO_PROVIDER_IDS.OKTA:
					return 'This is typically \'https://your-domain.okta.com/oauth2/default\'.'
				case SSO_PROVIDER_IDS.GOOGLE:
					return 'This is typically \'https://accounts.google.com\'.'
				default:
					return 'Base (authority) URL for OpenID Connect.'
			}
		},
		selectedProviderSignOutTooltip () {
			if (!this.ssoConfiguration?.ssoProviderId) {
				return 'The URL your identity provider uses to end a user session.'
			}
			switch (this.ssoConfiguration.ssoProviderId) {
				case SSO_PROVIDER_IDS.ADFS:
					return 'Use your ADFS logout endpoint (see federation metadata).'
				case SSO_PROVIDER_IDS.ENTRA_ID:
					return 'Typically \'https://login.microsoftonline.com/{tenant-id}/oauth2/logout/\'.'
				case SSO_PROVIDER_IDS.OKTA:
					return 'Typically \'https://your-domain.okta.com/logout\' (may include id_token_hint).'
				case SSO_PROVIDER_IDS.GOOGLE:
					return 'Typically \'https://accounts.google.com/logout\'.'
				default:
					return 'The URL used to terminate a user\'s session.'
			}
		}
	},
	methods: {
		updateEditConnectionDetails (value) {
			this.editConnectionDetails = value
		},
		updateSsoConfiguration (property, value) {
			const updated = { ...this.ssoConfiguration, [property]: value }

			if (property === 'isEnabled') {
				const noClientIdFromDb = !this.ssoConfiguration.originalClientId

				if (value && noClientIdFromDb) {
					updated.editConnectionDetails = true
				}
			}
			this.$emit('update:ssoConfiguration', updated)
		},
		addScope (valid) {
			if (!valid || !this.userFullPermissions || !this.scope) return
			const scopes = this.ssoConfiguration.scopes ? this.ssoConfiguration.scopes.split(',') : []
			scopes.push(this.scope)
			this.updateSsoConfiguration('scopes', scopes.join(','))
			this.scope = null
		},
		removeScope (scopeName) {
			const scopes = this.ssoConfiguration.scopes.split(',')
			const updatedScopes = scopes.filter(scope => scope !== scopeName)
			this.updateSsoConfiguration('scopes', updatedScopes.join(','))
		}
	}
}
</script>

<style scoped>
.selected-fields-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.square-chip {
	border-color: var(--global-border-color) !important;
	background-color: #ffffff !important;
	border-radius: var(--radius-lg, 12px) !important;
	height: 32px !important;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
	transition: all 0.2s ease;
}

.square-chip:hover {
	border-color: var(--global-border-color) !important;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	transform: translateY(-1px);
	background-color: var(--global-main-background-color) !important;
}

.square-chip :deep(.v-chip__content) {
	padding: 0 12px !important;
}

.square-chip :deep(.v-chip) {
	background-color: inherit !important;
	border: inherit !important;
}
</style>
